import clsx from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { handleMessageMultipleChanges } from "../../../../handleMessageMultipleChanges";
import { Button } from "../../../../tailwind/button";
import { Divider } from "../../../../tailwind/divider";
import {
  ErrorMessage,
  Field,
  Fieldset,
  Label,
  Legend,
} from "../../../../tailwind/fieldset";
import { Input } from "../../../../tailwind/input";
import SelectInput from "../../../../tailwind/select-input";
import { Textarea } from "../../../../tailwind/textarea";
import { Tooltip } from "../../../../Tooltip";

export const BodyFormSection = ({
  cards,
  body,
  setBody,
  bodyRef,
  bodyError,
  paramOptions,
  nodes,
  setNodes,
  selectedNode,
  errors,
}) => {
  const [parameterToInsert, setParameterToInsert] = useState("");

  const paramOptionsSet = useMemo(() => {
    const result = new Set();
    if (paramOptions?.length) {
      paramOptions.forEach((option) => {
        result.add(option.value);
      });
    }
    return result;
  }, [paramOptions]);

  const handleBodyChange = useCallback(
    (e) => {
      const updatedValue = e.target.value;
      setBody(updatedValue);

      let _bodyParameters = null;
      if (Object.keys(messageBodyParameterReferences)?.length) {
        _bodyParameters = Object.keys(messageBodyParameterReferences).map(
          (key) => ({
            selected: key,
            default: messageBodyParameterReferences[key] ?? "",
          }),
        );
      }
      handleMessageMultipleChanges(
        { body: updatedValue, bodyParameters: _bodyParameters },
        nodes,
        setNodes,
        selectedNode,
      );
    },
    [nodes, selectedNode, setBody, setNodes],
  );

  const updateBodyParameterReferences = (_defaultValue, _key) => {
    const _bodyParameters = Object.keys(messageBodyParameterReferences).map(
      (key) => ({
        selected: key,
        default:
          key === _key
            ? _defaultValue
            : (messageBodyParameterReferences[key] ?? ""),
      }),
    );
    handleMessageMultipleChanges(
      { bodyParameters: _bodyParameters },
      nodes,
      setNodes,
      selectedNode,
    );
  };

  const messageBodyParameterReferences = useMemo(() => {
    const _bodyParameterReferences = {};
    const { bodyParameters, body } = selectedNode?.data ?? {};

    const parametersFound = [
      ...(body ?? "").matchAll(/{{\s*([\w.]+)\s*}}/g),
    ].map((matches) => matches[1]);
    for (const variable of parametersFound) {
      _bodyParameterReferences[variable] = "";
    }

    for (const parameter of bodyParameters ?? []) {
      if (Object.hasOwn(_bodyParameterReferences, parameter.selected)) {
        _bodyParameterReferences[parameter.selected] = parameter.default ?? "";
      } else {
        delete _bodyParameterReferences[parameter.selected];
      }
    }

    return _bodyParameterReferences;
  }, [selectedNode?.data?.bodyParameters, selectedNode?.data?.body]);

  useEffect(() => {
    const _bodyParameters = Object.keys(messageBodyParameterReferences).map(
      (key) => ({
        selected: key,
        default: messageBodyParameterReferences[key] ?? "",
      }),
    );

    const currentBodyParameters = selectedNode?.data?.bodyParameters || [];
    const hasChanged =
      JSON.stringify(_bodyParameters) !== JSON.stringify(currentBodyParameters);

    if (hasChanged) {
      handleMessageMultipleChanges(
        { bodyParameters: _bodyParameters },
        nodes,
        setNodes,
        selectedNode,
      );
    }
  }, [messageBodyParameterReferences, nodes, selectedNode, setNodes]);

  const addBodyParameter = () => {
    const textarea = bodyRef.current;
    const textBefore = textarea.value.substring(0, textarea.selectionStart);
    const textAfter = textarea.value.substring(textarea.selectionEnd);
    let newCursorPosition;
    let updatedValue;

    const insertionText = `{{${parameterToInsert}}}`;
    updatedValue = `${textBefore}${insertionText}${textAfter}`;
    textarea.value = updatedValue;
    const { bodyParameters } = selectedNode?.data ?? {};
    const _bodyParameters = bodyParameters ?? [];
    const hasAlreadyBodyParameter = _bodyParameters.find(
      ({ selected }) => selected === parameterToInsert,
    );
    handleMessageMultipleChanges(
      {
        body: textarea.value,
        bodyParameters: hasAlreadyBodyParameter
          ? _bodyParameters
          : _bodyParameters.concat({
              selected: parameterToInsert,
              default: "",
            }),
      },
      nodes,
      setNodes,
    );
    setBody(textarea.value);
    newCursorPosition = textBefore.length + insertionText.length;

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  return (
    <Fieldset>
      <Field>
        {!cards && <Divider className="mt-5" />}

        <div className="flex justify-between items-end">
          <Legend>Body</Legend>
          <Tooltip
            size="4"
            content={
              <div>
                Text formatting:
                <br />
                *Asterisks make the text bold*
                <br />
                _Underscores make the text italic_
              </div>
            }
          />
        </div>
        <Textarea
          name="body"
          ref={bodyRef}
          value={body}
          onChange={handleBodyChange}
          invalid={bodyError}
          maxLength={1024}
          showCharacterCount
          data-testid="message-body-textarea"
        />
        {errors.bodyRequired && <ErrorMessage>Body is required</ErrorMessage>}

        <div className="flex mt-2 w-full justify-between">
          <div className="flex-grow mr-1">
            <SelectInput
              placeholder="Select a parameter"
              value={parameterToInsert}
              onChange={(value) => {
                setParameterToInsert(value);
              }}
              options={paramOptions}
              selected={parameterToInsert}
            />
          </div>

          <Button
            color="secondaryButton"
            disabled={parameterToInsert === ""}
            onClick={() => {
              addBodyParameter();
            }}
          >
            Add parameter
          </Button>
        </div>
      </Field>

      {Object.keys(messageBodyParameterReferences).map((key) => (
        <Field key={`body-parameter-reference-${key}`}>
          <Divider className="mt-5 mb-3" />
          <Label key={`body-parameter-reference-${key}-label`}>
            Parameter{" "}
            <span
              className={clsx(
                !paramOptionsSet.has(key) && !key.startsWith("global.")
                  ? "text-red-600"
                  : "",
                "font-bold",
              )}
            >
              {key}{" "}
              {!paramOptionsSet.has(key) &&
                !key.startsWith("global.") &&
                " (Unknown)"}
            </span>
          </Label>
          <div className="flex justify-between items-center">
            <Label key={`body-parameter-reference-${key}-default-value`}>
              Default value
            </Label>
            <Input
              className="max-w-[75%]"
              type="text"
              key={`body-parameter-reference-${key}-default-value-input`}
              placeholder="Default value if parameter is empty"
              autoComplete="off"
              value={messageBodyParameterReferences[key] ?? ""}
              onChange={(e) =>
                updateBodyParameterReferences(e.target.value, key)
              }
            />
          </div>
        </Field>
      ))}
    </Fieldset>
  );
};
